export interface Location {
  id: string;
  wilaya: string | null;
  daira: string | null;
  commune: string | null;
  latitude: number | null;
  longitude: number | null;
  wilaya_ar: string | null;
  commune_ar: string | null;
  daira_ar: string | null;
}

export type OfficialRole = string; // From settings: 'referee', 'assistant_referee_1', etc.

// Official type for mission order numbering (referee vs delegate)
export type OfficialType = 'referee' | 'delegate';

export enum PaymentStatus {
  PENDING_ENTRY = 'En attente de saisie',
  PENDING_VALIDATION = 'En attente de validation',
  REJECTED = 'Rejeté',
  READY_TO_PAY = 'Prêt à payer',
  PAID = 'Payé et Clôturé'
}


// Map new DB roles to a more descriptive frontend enum
export enum UserRole {
  SUPER_ADMIN = "super_admin",
  ADMIN = "admin",
  LEAGUE_PRESIDENT = "league_president",
  PLANNING_MANAGER = "planning_manager",
  COORDINATOR = "coordinator",
  ACCOUNTING_MANAGER = "accounting_manager",
  ACCOUNTANT_ENTRY = "accountant_entry",
  ACCOUNTANT_VALIDATOR = "accountant_validator",
  ACCOUNTANT_CLOSER = "accountant_closer",
  DISCIPLINARY_ADVOCATE = "disciplinary_advocate",
  DISCIPLINARY_SECRETARY = "disciplinary_secretary",
  AGENT = "agent",
  CONTRIBUTOR = "contributor",
  OFFICIAL = "official",
  REFREE_MANAGER = "refree_manager",
  TEAM_MANAGER = "team_manager",
  // Stadium media gate operator: scans accreditation badges at the turnstile and
  // reads the resulting access log. Named by the media RLS policies since the
  // module shipped, but until now it existed nowhere in this app, so it could
  // not be assigned from Paramètres → Utilisateurs and had zero rows live.
  SECURITY_GATE = "security_gate",
  VIEWER = "viewer"
}

// UI labels (French) used for display in the app.
export enum MatchStatus {
  SCHEDULED = "Prévu", // scheduled
  IN_PROGRESS = "En cours", // in_progress
  COMPLETED = "Joué", // completed
  POSTPONED = "Reporté", // postponed
  CANCELLED = "Annulé" // cancelled
}

// DB enum values for public.matches.status (Postgres enum match_status)
export enum DbMatchStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  POSTPONED = 'postponed',
  CANCELLED = 'cancelled',
}

// Aligned with DB ENUM 'goal_type'
export enum GoalType {
  OPEN_PLAY = "open_play",
  PENALTY = "penalty",
  FREE_KICK = "free_kick",
  HEADER = "header",
  OWN_GOAL = "own_goal",
  PENALTY_SHOOTOUT = "penalty_shootout",
  OTHER = "other"
}

// Aligned with DB ENUM 'goal_period'
export enum GoalPeriod {
  FIRST_HALF = "first_half",
  SECOND_HALF = "second_half",
  EXTRA_TIME = "extra_time",
  PENALTY_SHOOTOUT = "penalty_shootout"
}

// Aligned with new DB ENUM 'accounting_status'
export enum AccountingStatus {
  NOT_ENTERED = "not_entered", // Non saisi
  PENDING_VALIDATION = "pending_validation", // En attente de validation
  VALIDATED = "validated", // Validé
  REJECTED = "rejected", // Rejeté
  CLOSED = "closed" // Clos
}

// Aligned with new DB ENUM 'accounting_period_type'
export enum AccountingPeriodType {
  DAILY = "daily",
  MONTHLY = "monthly",
  PAYMENT_BATCH = "payment_batch"
}

// Aligned with new DB ENUM 'accounting_period_status'
export enum AccountingPeriodStatus {
  OPEN = "open",
  CLOSED = "closed"
}

// Aligned with new DB ENUM 'payment_correction_status'
export enum PaymentCorrectionStatus {
  PENDING_CORRECTION = "pending_correction",
  CORRECTION_IN_PROGRESS = "correction_in_progress",
  CORRECTED = "corrected",
  RECOVERY_PENDING = "recovery_pending",
  RECOVERY_COMPLETED = "recovery_completed",
  WRITTEN_OFF = "written_off"
}

// Aligned with new DB ENUM 'payment_batch_type'
export enum PaymentBatchType {
  REGULAR = "regular",
  CORRECTION = "correction"
}

// Aligned with new DB ENUM 'recovery_status'
// Legacy RecoveryStatus enum removed (single-payment corrections deprecated)

// Aligned with new DB ENUM 'correction_mode'
export enum CorrectionMode {
  IMMEDIATE = "immediate",  // Generate separate correction batch immediately
  DEFERRED = "deferred"     // Include in next regular payment batch
}

// Aligned with new DB ENUM 'batch_configuration'
export enum BatchConfiguration {
  SEPARATE = "separate",   // Corrections as distinct EDI lines
  MERGED = "merged"        // Corrections merged with regular payments for same official+month
}

// Aligned with new DB ENUM 'group_recovery_status'
export enum GroupRecoveryStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  PARTIALLY_RECOVERED = "partially_recovered",
  FULLY_RECOVERED = "fully_recovered",
  WRITTEN_OFF = "written_off"
}

export enum TransferEntityType {
  PLAYER = "player",
  STAFF = "staff",
}

export enum TransferStatus {
  DRAFT = "draft",
  PENDING = "pending",
  AWAITING_WINDOW = "awaiting_window",
  AWAITING_VALIDATION = "awaiting_validation",
  APPROVED = "approved",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  REJECTED = "rejected",
}

export enum TransferType {
  INTRA_LEAGUE = "intra_league",
  CROSS_LEAGUE = "cross_league",
  NEW_LEAGUE = "new_league",
  LOAN = "loan",
  FREE_AGENT = "free_agent",
}

/** Predefined transfer reasons for better tracking */
export enum TransferReason {
  MERCATO_WINTER = "mercato_winter",
  MERCATO_SUMMER = "mercato_summer",
  PLAYER_REQUEST = "player_request",
  CLUB_DECISION = "club_decision",
  END_OF_CONTRACT = "end_of_contract",
  LOAN_RETURN = "loan_return",
  DISCIPLINARY = "disciplinary",
  FINANCIAL = "financial",
  OTHER = "other",
}

export const TRANSFER_REASON_LABELS: Record<TransferReason, string> = {
  [TransferReason.MERCATO_WINTER]: "Mercato hivernal",
  [TransferReason.MERCATO_SUMMER]: "Mercato estival",
  [TransferReason.PLAYER_REQUEST]: "Demande du joueur",
  [TransferReason.CLUB_DECISION]: "Décision du club",
  [TransferReason.END_OF_CONTRACT]: "Fin de contrat",
  [TransferReason.LOAN_RETURN]: "Retour de prêt",
  [TransferReason.DISCIPLINARY]: "Raison disciplinaire",
  [TransferReason.FINANCIAL]: "Raison financière",
  [TransferReason.OTHER]: "Autre",
};

export enum TransferWindowStatus {
  SCHEDULED = "scheduled",
  OPEN = "open",
  CLOSED = "closed",
  ARCHIVED = "archived",
}

export enum TransferWindowKind {
  PRESEASON = "preseason",
  MIDSEASON = "midseason",
  EMERGENCY = "emergency",
  CUSTOM = "custom",
}

export enum PendingLeagueStatus {
  DRAFT = "draft",
  SUBMITTED = "submitted",
  APPROVED = "approved",
  REJECTED = "rejected",
  DUPLICATE = "duplicate",
}

export enum TransferMembershipStatus {
  ACTIVE = "active",
  DEPARTED = "departed",
  LOANED = "loaned",
  INACTIVE = "inactive",
}

export enum TransferEventType {
  STATUS_CHANGE = "status_change",
  NOTE = "note",
  DOCUMENT = "document",
  VALIDATION = "validation",
}


export interface RoleAssignment {
  role: UserRole | string;
  leagueIds?: string[]; // undefined or empty array means global scope for that role
  metadata?: Record<string, unknown>;
  assignedAt?: string;
  expiresAt?: string | null;
  isPrimary?: boolean;
}

export interface User {
  id: string;
  full_name: string;
  avatar_url: string | null;
  role: UserRole | string; // Backwards compatible primary role reference
  primaryRole?: UserRole | string;
  roles?: RoleAssignment[];
  leagueIds?: string[];
  teamId?: string | null;
  email?: string;
}

export interface AuditLog {
  id: string;
  timestamp: string;
  userId: string | null;
  userName: string | null;
  userEmail?: string;
  action: string;
  tableName?: string;
  recordId?: string;
  oldValues?: any;
  newValues?: any;
  userAgent?: string | null;
  ipAddress?: string | null;
  entityLabel?: string; // human readable label for the target entity (e.g. match teams/date, team name etc.)
}

export interface Unavailability {
  id: string;
  startDate: string; // start_date
  endDate: string;   // end_date
  reason?: string;
  isApproved: boolean;
}

export interface Official {
  id: string;
  firstName: string;
  lastName: string;
  firstNameAr: string;
  lastNameAr: string;
  fullName: string;
  category: string; // official_category enum
  locationId: string | null;
  address: string | null;
  position: number | null;
  email: string | null;
  /**
   * Future-proof multi-email support. While migrating we keep legacy `email` for backward compatibility.
   * If provided, `emails` must be a list of unique, lower-cased addresses. The first item is considered primary.
   */
  emails?: string[];
  phone: string | null;
  bankAccountNumber: string | null;
  dateOfBirth: string | null;
  isActive: boolean;
  isArchived: boolean;
  userId: string | null;
  unavailabilities: Unavailability[];
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

export interface Assignment {
  id: string;
  matchId: string;
  role: OfficialRole; // assignment_role enum
  officialId: string | null;
  originalOfficialId?: string | null;
  isConfirmed: boolean;
  confirmedAt: string | null;
  isRejectedByOfficial?: boolean;
  rejectionReason?: string | null;
  rejectionComment?: string | null;
  travelDistanceInKm: number | null;
  indemnityAmount: number | null;
  notes: string | null;
  /** True when the assigned official did not officiate (no payment due). */
  isAbsent?: boolean;
  /** True when the slot is intentionally left without an official but considered compliant. */
  isNotAssigned?: boolean;
  /** True when this assignment has already been paid in a payment batch. */
  isPaid?: boolean;
  /** Payment completion timestamp when available. */
  paidAt?: string | null;
  /** Accounting period / payment batch identifier used for the transfer. */
  paymentBatchId?: string | null;
  /** The base imposable indemnity amount before adjustments and taxes. */
  baseIndemnity?: number | null;
  /** The calculated IRG tax amount at the time of validation. */
  irgAmount?: number | null;
  /** The final net amount to be paid (gross - tax). */
  netAmount?: number | null;
  /** Distance bonus (10,000 DZD if travel >= 1000km, non-taxable). */
  distanceBonus?: number | null;
  /** Human-friendly reference for the payment batch when present. */
  paymentBatchReference?: string | null;
  /** Persisted payment amount coming from payment batches (EDI). */
  paymentAmount?: number | null;
  /** Legacy/raw payment amount mirror to match Supabase column naming. */
  payment_amount?: number | null;
  isNew?: boolean;
  createdBy?: string;
  lastMissionOrderSentAt?: string | null;
  /** Embedded official name from join (available in kanban queries). */
  officialName?: string | null;
  /** Embedded official location ID from join. */
  officialLocationId?: string | null;
  /** Embedded official (`official:officials(*)`) — lets another league's designation
   *  show its official even when he is not in the viewer's league list. */
  official?: Official | null;
}

export interface TeamLeagueMembership {
  leagueId: string;
  leagueGroupId?: string | null;
  season?: string | null;
}

/** UUID fixe de la catégorie Séniors (migration 20261007100000_categories). */
export const SENIOR_CATEGORY_ID = '00000000-0000-4000-8000-000000000001';

/** Catégorie d'âge FAF (Séniors, U20, U17…). */
export interface Category {
  id: string;
  code: string;
  nameFr: string;
  nameAr: string;
  /** Id cms FAF /configuration/categories. */
  fafCategoryId: number;
  sortOrder: number;
  isYouth: boolean;
}

export interface Team {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  fullName: string | null;
  abriv: string | null;
  nameFaf: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  foundedYear: number | null;
  isArchived: boolean;
  /** True if this team was auto-created to represent an external opponent (e.g. Coupe, amical, etc.) */
  isExternal?: boolean; // not yet in DB schema -> forward-compatible
  /** Optional free-text origin / competition name for external placeholder teams */
  externalOrigin?: string | null; // forward-compatible metadata
  createdAt: string;
  createdBy: string | null;
  createdByName: string;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  leagueMemberships?: TeamLeagueMembership[];
  wilaya?: string | null;
  /** FAF engagement tier label (e.g. "Ligue 3", "Régionale 1", "Honneur"). */
  engagement?: string | null;
  /** Identifiant du club côté FAF (NULL = équipe créée à la main). */
  fafId?: number | null;
  /** Catégorie d'âge de l'équipe (SENIOR pour un club). */
  categoryId: string;
  /** NULL = la ligne est le club (et son équipe senior) ; sinon le club de cette équipe de catégorie. */
  clubTeamId: string | null;
}

export interface Stadium {
  id: string;
  name: string;
  nameAr: string | null;
  locationId: string | null;
  latitude: number | null;
  longitude: number | null;
  isArchived: boolean;
  createdAt: string;
  createdBy: string | null;
  createdByName: string;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

export interface TransferWindow {
  id: string;
  leagueId: string | null;
  season: string;
  label: string;
  windowKind: TransferWindowKind;
  status: TransferWindowStatus;
  startAt: string;
  endAt: string;
  isGlobal: boolean;
  metadata: Record<string, unknown>;
}

export interface PendingLeagueProposal {
  id: string;
  proposedName: string;
  proposedNameAr?: string | null;
  proposedCode?: string | null;
  level?: number | null;
  region?: string | null;
  status: PendingLeagueStatus;
  requestedBy: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  duplicateOf?: string | null;
  approvedLeagueId?: string | null;
  validationNotes?: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface TransferEvent {
  id: string;
  transferId: string;
  eventType: TransferEventType;
  fromStatus?: TransferStatus | null;
  toStatus?: TransferStatus | null;
  note?: string | null;
  payload: Record<string, unknown>;
  notedAt: string;
  notedBy: string | null;
}

export interface TransferRecord {
  id: string;
  entityType: TransferEntityType;
  entityId: string;
  entityDisplayName: string;
  entitySnapshot: Record<string, unknown>;
  sourceLeagueId: string | null;
  sourceLeagueName?: string | null;
  sourceTeamId: string | null;
  sourceTeamName?: string | null;
  destinationLeagueId: string | null;
  destinationLeagueName?: string | null;
  destinationTeamId: string | null;
  destinationTeamName?: string | null;
  transferType: TransferType;
  status: TransferStatus;
  mercatoPeriodId: string | null;
  season: string | null;
  requestedBy: string | null;
  approvedBy?: string | null;
  requestedAt: string;
  approvedAt?: string | null;
  effectiveDate?: string | null;
  newLicenseNumber?: string | null;
  newPlayerNumber?: number | null;
  notes?: string | null;
  reason?: string | null;
  attachments: unknown[];
  metadata: Record<string, unknown>;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  events?: TransferEvent[];
}

export interface TransferHistoryEntry extends TransferRecord {
  events: TransferEvent[];
}

export interface TransferFormSubmission {
  entityType: TransferEntityType;
  entityId: string;
  entityDisplayName: string;
  entitySnapshot?: Record<string, unknown>;
  sourceLeagueId: string | null;
  sourceLeagueName?: string | null;
  sourceTeamId: string | null;
  sourceTeamName?: string | null;
  destinationLeagueId: string | null;
  destinationLeagueName?: string | null;
  destinationTeamId: string | null;
  destinationTeamName?: string | null;
  destinationPendingLeagueId?: string | null;
  destinationPendingTeam?: Record<string, unknown> | null;
  transferType: TransferType;
  mercatoPeriodId?: string | null;
  season?: string | null;
  effectiveDate?: string | null;
  newLicenseNumber?: string | null;
  newPlayerNumber?: number | null;
  notes?: string | null;
  reason?: string | null;
  attachments?: unknown[];
  metadata?: Record<string, unknown>;
}

export type LeagueKind = 'league' | 'cup';

export interface League {
  id: string;
  name: string;
  name_ar: string;
  level: number;
  parent_league_id: string | null;
  code?: string | null;
  is_active?: boolean;
  kind?: LeagueKind;
}

export type LeagueGroupKind = 'regular' | 'cup';

export interface LeagueGroup {
  id: string;
  name: string;
  name_ar: string;
  league_id: string;
  season: string;
  teamIds: string[];
  /** 'regular' = round-robin season group; 'cup' = league-organized cup (excluded from standings, treated as league match for accounting/planning/missions). */
  kind: LeagueGroupKind;
  /** Human label for cup competitions. Required when kind='cup'; null otherwise. */
  organizedCompetitionName: string | null;
  /** Arabic phase label rendered under "الجولة N" in the PV (e.g. "دورة اللقب النصف النهائي"). NULL = nothing rendered. */
  tournament_phase_ar?: string | null;
  /** Cup only: qualifying sources, each with a top-N quota. e.g. [{group_id, top_n: 2}] */
  qualifies_from?: Array<{ group_id: string; top_n: number }>;
  /** Cup only: destination league of the cup-winner promotion (NULL = top tier / no promotion). */
  promotes_winner_to_league_id?: string | null;
  /** Points to the source group this was cloned from during a season carry-forward. NULL for hand-created groups. */
  cloned_from_group_id?: string | null;
  /** Configured target team count for the group's season (capacity); null when unset. */
  targetTeamCount?: number | null;
  /** Catégorie d'âge du groupe (SENIOR par défaut). */
  categoryId: string;
  /** Id de groupe du cms FAF (unique par saison). */
  fafGroupId?: number | null;
  /** Id de compétition du cms FAF. */
  fafCompetitionId?: number | null;
}

export interface Match {
  id: string;
  season: string;
  gameDay: number;
  leagueGroup: {
    id: string;
    name: string;
    name_ar: string;
    /** 'regular' for championship groups, 'cup' for league-organized cup groups. Optional for backward compatibility with older API responses. */
    kind?: LeagueGroupKind;
    /** Cup label surfaced to the UI (only set when kind='cup'). */
    organizedCompetitionName?: string | null;
    league: {
      id: string;
      name: string;
      name_ar: string;
    }
  };
  matchDate: string | null; // match_date
  matchTime: string | null; // match_time
  homeTeam: Team;
  awayTeam: Team;
  homeScore: number | null;
  awayScore: number | null;
  stadium: Stadium | null;
  status: MatchStatus;
  assignments: Assignment[];
  isSheetSent: boolean;
  hasUnsentChanges: boolean;
  isArchived: boolean;
  /** True if this match is part of an external competition (e.g., Coupe d'Algérie) whose cards aggregate with league discipline */
  isExternalCompetition?: boolean; // is_external_competition
  /** Optional human-readable external competition name */
  externalCompetitionName?: string | null; // external_competition_name
  /** League that organises this external competition match and handles it (designations, roles, mission orders) */
  originatingLeagueId?: string | null; // originating_league_id
  /** Cup round (cup_rounds.code) of a cup match (isCupMatch); null otherwise or when not set. Use it, not gameDay, to label/group a cup match. */
  cupRound?: string | null; // cup_round
  /** True if this match is played behind closed doors (à huis clos) without fans. Affects which stamp is used in mission order PDFs. */
  isNoFans?: boolean; // is_no_fans
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  // Accounting fields
  accountingStatus: AccountingStatus;
  rejectionReason: string | null;
  rejectionComment: string | null;
  validatedBy: string | null;
  validatedByName?: string;
  validatedAt: string | null;
  accountingPeriodId: string | null;
  goals?: MatchGoal[];
  goalSummary?: MatchGoalSummary;
  disciplinaryEntryStatus: DisciplinaryMatchEntryStatus;
  disciplinaryEntryStatusUpdatedAt: string | null;
  disciplinaryEntryStatusUpdatedBy: string | null;
  forfait_type?: ForfaitType | null;
  forfaitReason?: string | null;
  sanctionsSummary?: {
    home: { yellow: number; red: number; player: { yellow: number; red: number }; staff: { yellow: number; red: number }; team: { count: number }; fines: number };
    away: { yellow: number; red: number; player: { yellow: number; red: number }; staff: { yellow: number; red: number }; team: { count: number }; fines: number };
    total: number;
    totalFines: number;
  };
  // Report status derived from counts
  reports?: {
    hasEvents: boolean;
    hasIncidents: boolean;
    hasNotes: boolean;
    eventsCount: number;
    incidentsCount: number;
    notesCount: number;
  };
  /** FAF final-report PDF (Arabic) — populated when faf_matches row is joined */
  fafFinalArUrl?: string | null;
  /** True when at least one faf_matches row is linked to this match */
  hasFafData?: boolean;
}

export interface MatchGoal {
  id: string;
  matchId: string;
  teamId: string;
  teamName?: string | null;
  playerId: string | null;
  playerName?: string | null;
  displayScorerName: string;
  displayScorerNameAr?: string | null;
  scorerNameOverride?: string | null;
  assistPlayerId: string | null;
  assistName?: string | null;
  displayAssistName?: string | null;
  displayAssistNameAr?: string | null;
  assistNameOverride?: string | null;
  goalType: GoalType;
  period: GoalPeriod;
  minute: number | null;
  stoppageTime: number | null;
  countsTowardScore: boolean;
  isOwnGoal: boolean;
  description?: string | null;
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string | null;
  updatedBy?: string | null;
  fafEventId?: string | null;
}

export interface MatchGoalInput {
  id?: string;
  teamId: string;
  playerId?: string | null;
  assistPlayerId?: string | null;
  scorerNameOverride?: string | null;
  assistNameOverride?: string | null;
  goalType?: GoalType;
  period?: GoalPeriod;
  minute?: number | null;
  stoppageTime?: number | null;
  countsTowardScore?: boolean;
  isOwnGoal?: boolean;
  description?: string | null;
  fafEventId?: string | null;
}

export interface MatchGoalSummary {
  regulation: {
    home: number;
    away: number;
  };
  special: {
    home: number;
    away: number;
  };
}

export interface AccountingPeriod {
  id: string;
  type: AccountingPeriodType;
  periodDate: string; // period_date
  status: AccountingPeriodStatus;
  createdAt: string;
  updatedAt: string | null;
  closedBy: string | null;
  closedByName?: string;
  closedAt: string | null;
  reopenedBy: string | null;
  reopenedByName?: string;
  reopenedAt: string | null;
  leagueGroupId?: string | null;
  leagueId: string;
  gameDay?: number | null;
  season?: string | null;
}

export interface PaymentBatch extends AccountingPeriod {
  reference: string;
  totalAmount: number;
  paymentCount: number;
  debitAccountNumber: string;
  transactionId?: string | null;
  proofOfPaymentUrl?: string | null;
  proofOfPaymentFilename?: string | null;
  ediFilePath?: string | null; // Path to stored EDI file in Supabase storage
  ediFileName?: string | null; // Original EDI filename for display
  ediGeneratedAt?: string | null; // Timestamp when EDI was generated
  // Payment correction fields
  batchType?: PaymentBatchType; // Type: regular or correction batch
  correctsBatchId?: string | null; // Reference to original batch if correction
  correctionCount?: number; // Number of corrections in this batch
  // Deferred correction tracking
  includesDeferredCorrections?: boolean;
  deferredCorrectionCount?: number;
  deferredCorrectionConfig?: any; // JSONB field
}

// Recovery document interface for tracking supporting documents
// Legacy PaymentCorrection interfaces removed (group corrections supersede single-payment corrections)

// Payment correction group - aligns corrections with how payments are aggregated (official + month)
export interface PaymentCorrectionGroup {
  id: string;

  // Group identification
  officialId: string;
  monthKey: string; // Format: 'YYYY-MM'
  categoryFilter?: string | null; // 'arbitres', 'delegues', or 'auditeurs'

  // Original payment tracking
  originalBatchId: string;
  originalBatchReference: string;
  originalTotalAmount: number; // Sum of all payments in this group
  originalPaymentCount: number; // How many individual payments in this group

  // Correction details
  correctionReason: string;
  oldRib: string; // RIB that was used in original erroneous payment
  newRib: string; // Correct RIB to use

  // Correction configuration
  correctionMode: CorrectionMode;
  batchConfiguration: BatchConfiguration;

  // Corrective payment tracking
  correctedBatchId?: string | null;
  correctedBatchReference?: string | null;
  correctedTotalAmount?: number | null;
  correctedAt?: string | null;

  // Group-level recovery tracking
  totalRecoveryAmount: number;
  recoveryStatus: GroupRecoveryStatus;
  recoveryNotes?: string | null;

  // Status tracking
  status: PaymentCorrectionStatus;

  // Audit trail
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string;

  // Metadata
  metadata?: Record<string, unknown>;

  // Enriched fields for display
  officialName?: string;
  officialNameAr?: string | null;
  leagueId?: string;
}

// Links correction groups to individual assignments
export interface CorrectionGroupAssignment {
  id: string;
  correctionGroupId: string;
  assignmentId: string;
  assignmentType: 'match' | 'mission';
  paymentAmount: number;
  individualRecoveryAmount: number;
  createdAt: string;
}

// Deferred corrections queue entry
export interface DeferredCorrection {
  id: string;
  correctionGroupId: string;
  queuedAt: string;
  priority: number;
  batchConfiguration: BatchConfiguration;
  processed: boolean;
  processedAt?: string | null;
  processedInBatchId?: string | null;
  notes?: string | null;
  targetMonth?: string | null; // Format: YYYY-MM, specifies which month this correction should be paid in

  // Enriched with correction group data
  correctionGroup?: PaymentCorrectionGroup;
}

// Aggregated payment group for UI display (used in both pending and paid views)
export interface AggregatedOfficialPayment {
  // Unique identifier for this aggregated group
  id?: string;

  // Group identification
  officialId: string;
  officialName: string;
  officialNameAr?: string | null;
  officialCategory: string;
  monthKey: string; // Format: 'YYYY-MM'
  monthLabel?: string; // Human-readable month label (e.g., "Janvier 2025")
  categoryFilter?: string; // 'arbitres', 'delegues', or 'auditeurs'

  // Aggregated amounts
  totalAmount: number;
  totalGross: number;
  totalIrg: number;
  paymentCount: number;

  // Detailed financial breakdown (optional - for compatibility with existing code)
  totals?: any; // AggregatedFinancialTotals from utils/financial
  baseTotal?: number;
  adjustmentTotal?: number;
  hasUnknownDate?: boolean;

  // Individual payments in this group
  payments: Payment[];

  // Payment tracking (for paid groups)
  isPaid?: boolean;
  paidAt?: string | null;
  paymentBatchId?: string | null;
  paymentBatchReference?: string | null;

  // Correction tracking (for paid groups)
  hasCorrectionPending?: boolean;
  correctionGroupId?: string | null;
  correctionStatus?: PaymentCorrectionStatus | null;

  // Deferred correction integration (for pending groups)
  includesCorrections?: boolean; // True if this group includes deferred correction payments
  correctionAmount?: number; // Total amount of corrections in this group
  deferredCorrectionIds?: string[]; // IDs of deferred corrections included in this group

  // Bank details
  bankAccountNumber?: string | null;
  ribValid?: boolean;
}

export type NavigationTab = 'planning' | 'classements' | 'disciplinary' | 'officials' | 'finances' | 'clubs' | 'settings' | 'audit' | 'accounting' | 'virements' | 'corrections' | 'etats';

// Mission Order interfaces
export interface MissionOrder {
  id: string;
  created_at: string;
  order_number: number;
  order_number_display: string; // Formatted: "A-142", "D-037"
  match_id: string | null;
  mission_assignment_id: string | null;
  mission_id: string | null;
  official_id: string;
  official_type: OfficialType;
  league_id: string;
  pdf_storage_path: string | null;
  data_hash: string | null;
  data_snapshot: any | null; // JSONB
  context_type: 'match' | 'mission';
  is_superseded: boolean; // True if order is obsolete due to reassignment
}

export interface MissionOrderCounter {
  id: string;
  league_id: string;
  official_type: OfficialType;
  current_number: number;
  last_updated: string;
  created_at: string;
}

export type IndemnityRates = {
  [leagueId: string]: { [role in OfficialRole]?: number };
};

export interface TeamHistoryEntry {
  season: string;
  leagueName: string;
  groupName: string;
  leagueId: string;
}

// Synthetic type for the Finances view, derived from match_assignments OR mission_assignments
export interface Payment {
  id: string; // assignment id (match or mission)
  officialId: string;
  matchId?: string | null; // Optional: only for match-based payments
  missionId?: string | null; // Optional: only for mission-based payments
  assignmentType?: 'match' | 'mission'; // Type discriminator
  missionKindCode?: string | null; // e.g. 'stadium_audit' | 'manual_mission'
  leagueId: string;
  groupId?: string | null; // Optional: missions don't have league groups
  gameDay?: number | null; // Optional: only for match-based payments
  matchDescription?: string; // Or mission description
  matchDate: string; // Or scheduled date
  officialName: string;
  officialNameAr?: string | null;
  officialCategory?: string | null;
  bankAccountNumber?: string | null;
  role: OfficialRole;
  indemnity: number; // This is the GROSS amount
  baseIndemnity?: number | null; // The recorded base indemnity (imposable) - used for missions
  travelDistanceInKm: number;
  irgAmount: number | null; // The calculated tax amount
  total: number; // This is now the NET amount
  accountingStatus: AccountingStatus;
  notes: string | null;
  originalOfficialId?: string | null;
  validatedByUserId: string | null;
  validatedAt: string | null;
  validatedByName?: string;
  rejectionReason?: string | null;
  rejectionComment?: string | null;
  // Payment tracking fields
  isPaid?: boolean; // True when included in a payment batch
  paidAt?: string | null; // Timestamp when payment batch was closed
  paymentBatchId?: string | null; // Reference to the payment batch
  paymentBatchReference?: string | null; // Batch reference for display
  // Payment correction fields
  correctionStatus?: PaymentCorrectionStatus | null; // If set, payment needs correction or has been corrected
  // paymentCorrectionId removed (legacy single-payment correction system)
  // Manual month override for payment assignment
  paymentMonthOverride?: string | null; // Format: 'YYYY-MM', overrides automatic gameDay-based month logic
  // Match de coupe (externe ou groupe kind='cup') : mois de paiement = mois de la date, jamais celui de la journée
  isCupMatch?: boolean;
  /** Tour (cup_rounds.code) d'un match de coupe ; null hors coupe ou tour non précisé. Pour l'affichage seulement. */
  cupRound?: string | null;
}


export interface TeamSeasonStadium {
  id: string;
  teamId: string;
  stadiumId: string;
  season: string;
}

export interface FinancialSettings {
  irgRatePercent: number;
  // Added optional price_per_km surfaced in settings UI (stored as top-level column in app_settings_versions, duplicated here for edit convenience)
  price_per_km?: number;
  // Stadium audit fixed indemnity amount (per audit, regardless of role)
  stadiumAuditAmount?: number;
}

export interface DisciplinarySettings {
  yellowCardThreshold: number;
  yellowCardSuspension: number;
  directRedCardSuspension: number;
  twoYellowsRedCardSuspension: number;
  /**
   * Optional advanced accumulation rules. Each threshold triggers exactly once
   * when the player's total active yellow cards reaches the threshold value.
   * Example: [{threshold:4,suspension:1},{threshold:8,suspension:1},{threshold:12,suspension:2}]
   * If undefined or empty, legacy single yellowCardThreshold/yellowCardSuspension are used.
   */
  accumulationRules?: { threshold: number; suspension: number }[];
  /**
   * @deprecated This JSON-based approach is deprecated. Use disciplinary_reasons and league_disciplinary_rules tables instead.
   * See types/disciplinary-refactor.types.ts for new types.
   *
   * Managed list of predefined sanction reasons each with an associated monetary fine amount.
   * Stored inside disciplinary_settings JSON (versioned). isActive allows soft hiding without losing historical mapping.
   * defaultSuspensionMatches: optional default number of match suspensions to auto-apply when this reason is selected.
   */
  sanctionReasons?: { id: string; label: string; amount: number; defaultSuspensionMatches?: number; isActive?: boolean }[];
  /**
   * Enable mid-season yellow card reset based on gameDay.
   * Cards from first half won't count toward thresholds in second half.
   * Existing suspension debt from first half still carries over.
   */
  enableMidSeasonReset?: boolean;
  /**
   * Total game days in the season (e.g., 34).
   * Mid-season = floor(totalGameDays / 2).
   * Only used when enableMidSeasonReset is true.
   */
  totalGameDays?: number;
  /**
   * Réglage de LIGUE seulement (league_settings_versions.config.discipline) :
   * 'group' = la mi-saison d'un joueur est celle du groupe de son équipe
   * (moitié de la longueur du groupe). Absent / 'league' = comportement actuel.
   */
  midSeasonSplit?: "league" | "group";
}

export interface OptimizationSettings {
  travelSpeedKmph: number;
  bufferMin: number;
  matchDurationMin: number;
  defaultMatchRisk: number;
  categoryGradeMap: { [category: string]: number };
  categoryCapacityMap: { [category: string]: number };
  /**
   * Optional list of league IDs that restrict mission order generation to officials whose role contains "délégué".
   * Applied both in individual and bulk flows (planning modal, focus mode, exports).
   */
  missionOrderDelegateOnlyLeagueIds?: string[];
}

// --- Disciplinary Module Types ---

export enum SanctionType {
  YELLOW_CARD = 'YELLOW_CARD',
  RED_CARD_DIRECT = 'RED_CARD_DIRECT',
  RED_CARD_TWO_YELLOWS = 'RED_CARD_TWO_YELLOWS',
  SUSPENSION_COMMISSION = 'SUSPENSION_COMMISSION',
  FINE = 'FINE',
  SUSPENSION = 'SUSPENSION',
  WARNING = 'WARNING',
  REPORT = 'REPORT',
  OTHER = 'OTHER'
}

export enum DisciplinarySubjectType {
  PLAYER = 'player',
  COACH = 'coach',
  STAFF = 'staff',
  TEAM = 'team'
}

export enum DisciplinarySuspensionType {
  DATE_RANGE = 'DATE_RANGE',
  /** Legacy DB value "TIME" — treated identically to DATE_RANGE by the edge function. Frontend must also handle this. */
  TIME = 'TIME',
  /** No suspension applies for this violation */
  NONE = 'NONE',
  /** Suspension measured in number of matches */
  MATCH_COUNT = 'MATCH_COUNT',
  /** Suspension measured in days/time period */
  TIME_BASED = 'TIME_BASED',
  /** Both match count and time-based suspension apply */
  BOTH = 'BOTH',
}

export enum DisciplinaryMatchEntryStatus {
  WAITING = 'waiting',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed'
}

export enum DisciplinarySessionStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  CLOSED = 'closed'
}

export enum DisciplinaryCaseStatus {
  DRAFT = 'draft',
  DELIBERATED = 'deliberated',
  PUBLISHED = 'published',
  ARCHIVED = 'archived'
}

export type TeamStaffGroup = 'coach' | 'staff';

export interface TeamStaffMember {
  id: string;
  /** Photo brute / détourée (RPC d'Effectifs `photo_url` / `processed_photo_url`). */
  photoUrl?: string | null;
  processedPhotoUrl?: string | null;
  teamId: string;
  groupType: TeamStaffGroup;
  role: string;
  firstName: string;
  lastName: string;
  fullName: string;
  firstNameAr: string | null;
  lastNameAr: string | null;
  fullNameAr: string | null;
  dateOfBirth: string | null;
  licenseNumber: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  isArchived: boolean;
  notes?: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName?: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName?: string | null;
  sanctionCount?: number | null;
  sanctionsCount?: number | null;
  latestSanctionDate?: string | null;
  lastSanctionDate?: string | null;
}

export interface DisciplinarySession {
  id: string;
  /** league_id — the league owning the PV (not necessarily the store's selection). */
  leagueId?: string | null;
  season: string;
  meetingDate: string; // meeting_date
  meetingTime?: string | null; // meeting_time
  meetingNumber?: number | null;
  committeeName: string;
  location?: string | null;
  status: DisciplinarySessionStatus;
  quorum?: string | null;
  chairperson?: string | null;
  secretary?: string | null;
  attendees?: string | null;
  agenda?: string | null;
  notes?: string | null;
  // PV generator fields
  publicationNumber?: string | null;
  /** Objet du PV, une ligne de page de garde par ligne. */
  subjectAr?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  summonsRecipientAr?: string | null;
  summonsDeadline?: string | null;
  /** Ordered case_type keys controlling PV section order. NULL = default. */
  sectionOrder?: string[] | null;
  committeeMembers?: Array<{ role: string; nameAr: string; displayOrder: number }>;
  /** Dépôt sur lirf.dz (publish-pv-to-site) ; lecture seule côté client. */
  siteReportId?: number | null;
  siteFileUrl?: string | null;
  siteArticleId?: number | null;
  siteNewsArticleId?: string | null;
  sitePublishedAt?: string | null;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  activeCaseCount?: number | null;
  totalCaseCount?: number | null;
  firstDecisionDate?: string | null;
  lastDecisionDate?: string | null;
}
export interface DisciplinaryCase {
  id: string;
  sessionId?: string | null;
  matchId?: string | null;
  matchDate?: string | null;
  matchSeason?: string | null;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  leagueGroupId?: string | null;
  caseNumber?: string | null;
  status: DisciplinaryCaseStatus;
  title: string;
  // PV generator fields
  caseType?: 'match_sanction' | 'appeal_committee' | 'summons' | 'settlement' | 'administrative_protest' | 'correction_note' | 'fine_cancellation' | 'administrative_case' | 'fine_addition' | 'free_text';
  displayOrder?: number | null;
  caseData?: Record<string, unknown> | null;
  /** Match section: ruling text printed between the preamble and the team tables. */
  bodyNarrativeAr?: string | null;
  /** Match section: NULL / "decided" = team tables; the others short-circuit them. */
  caseOutcome?: "decided" | "transferred_to_competition" | "transferred_to_appeal" | "forfeit" | "withdrawal" | null;
  hearingOrder?: number | null;
  subjectType?: DisciplinarySubjectType | null;
  playerId?: string | null;
  staffMemberId?: string | null;
  teamId?: string | null;
  playerName?: string | null;
  staffMemberName?: string | null;
  teamName?: string | null;
  subjectSnapshot?: Record<string, unknown> | null;
  subjectSummary?: string | null;
  allegations?: string | null;
  factsEstablished?: string | null;
  analysis?: string | null;
  decision?: string | null;
  narrativeSummary?: string | null;
  decisionDate?: string | null;
  sanctionsSummary?: string | null;
  recommendations?: string | null;
  notes?: string | null;
  attachments?: Record<string, unknown> | null;
  createdAt: string;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  publishedAt?: string | null;
  session?: DisciplinarySession | null;
}

export interface DisciplinarySubjectSummary {
  subjectId: string;
  subjectType: DisciplinarySubjectType;
  displayName: string;
  teamId: string | null;
  licenseNumber?: string | null;
  groupType?: TeamStaffGroup | null;
  role?: string | null;
}

export interface Player {
  id: string;
  /** Photo brute / détourée (RPC d'Effectifs `photo_url` / `processed_photo_url`). */
  photoUrl?: string | null;
  processedPhotoUrl?: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  firstNameAr?: string;
  lastNameAr?: string;
  dateOfBirth: string | null;
  licenseNumber: string;
  playerNumber: number | null;
  currentTeamId: string | null;
  isArchived: boolean;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

export interface Sanction {
  id: string;
  leagueId: string;
  /** NEW: Link to disciplinary_reasons table */
  reasonId?: string | null;
  /** NEW: Joined disciplinary reason object (when fetched with JOIN) */
  disciplinaryReason?: {
    id: string;
    code: string;
    reasonAr: string;
    reasonFr: string | null;
    description?: string | null;
  };
  subjectType: DisciplinarySubjectType;
  playerId?: string | null;
  staffMemberId?: string | null;
  teamId?: string | null;
  matchId: string | null;
  entryContextMatchId?: string | null;
  caseId?: string | null;
  type: SanctionType;
  /** @deprecated Use reasonId and disciplinaryReason instead. Text-based reason field kept for backward compatibility. */
  reason?: string;
  decisionDate: string;
  suspensionType: DisciplinarySuspensionType;
  suspensionMatchCount?: number | null;
  /** Sursis (suspended) portion of the suspension, in the same unit as suspensionMatchCount. Renders as "منها N موقوفة النفاذ" in the PV. */
  suspensionSuspendedValue?: number | null;
  suspensionStartDate?: string | null;
  suspensionEndDate?: string | null;
  suspensionMatches: number;
  fineAmount?: number;
  matchesServed: number;
  isCancelled: boolean;
  /** FK to the disciplinary_cases row (type=fine_cancellation) that cancelled this sanction. */
  cancelledByCaseId?: string | null;
  /** Embedded cancelling case metadata (when joined via useSanctions). */
  cancelledByCase?: {
    id: string;
    title?: string | null;
    case_number?: string | null;
    session_id?: string | null;
    disciplinary_session?: {
      id: string;
      publication_number?: string | null;
      meeting_date?: string | null;
    } | null;
  } | null;
  notes?: string;
  subjectNotes?: string;
  // Machine-readable structured metadata (future-proof vs parsing notes)
  // auto.kind: e.g. 'yellow_card_accumulation'
  // auto.threshold: threshold that triggered the auto sanction
  meta?: { auto?: { kind: string; threshold?: number } };
  createdAt: string;
  createdBy: string;
  createdByName: string;
  createdVia?: 'manual' | 'auto' | 'match_entry' | string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  subjectDisplayName?: string | null;
  subjectLicenseNumber?: string | null;
  teamName?: string | null;
  staffRole?: string | null;
  caseNumber?: string | null;
  caseTitle?: string | null;
  caseStatus?: DisciplinaryCaseStatus;
  sessionId?: string | null;
  linkedCase?: DisciplinaryCase | null;

  /** FK to faf_match_events.id — set when sanction was imported from FAF */
  fafEventId?: string | null;

  /** FK to infractions.id — canonical infraction code (replaces reason_id long-term) */
  infractionId?: string | null;
  /** Modifier JSONB — shapes the applied rule. {target, offense_level, subject_type, outcome, context, ...} */
  modifier?: Record<string, unknown>;
  /** Snapshot of the applied consequence at decision time (fine, suspension, PV articles and label) */
  appliedConsequence?: {
    fine?: number | null;
    suspension?: { unit: 'matches' | 'days' | 'months'; value: number; start_date?: string; end_date?: string } | null;
    articles?: string[];
    accumulates_booking?: boolean;
    notes?: string;
    /** The motif's « Libellé PV » snapshot, set by the database. */
    label_ar?: string;
  } | null;
  /** Optional match snapshot returned by certain RPCs (JSONB). */
  match?: any | null;
  /** Optional entry-context match snapshot returned by certain RPCs (JSONB). */
  entryContextMatch?: any | null;
}

// --- FINE PAYMENTS ---

export type FinePaymentMethod = 'cash' | 'bank_transfer' | 'check' | 'other';
export type FinePaymentStatus = 'unpaid' | 'partial' | 'paid' | 'cancelled';

export interface FinePayment {
  id: string;
  sanctionId: string | null;
  teamId: string;
  leagueId: string;
  season: string;
  amount: number;
  paymentDate: string;
  paymentMethod: FinePaymentMethod;
  referenceNumber: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
  isCancelled: boolean;
}

export interface TeamFinesSummaryRow {
  teamId: string;
  teamName: string;
  teamNameAr: string | null;
  totalFinesAmount: number;
  totalFinesCount: number;
  totalPaidAmount: number;
  totalPaidCount: number;
  outstandingBalance: number;
  lastPaymentDate: string | null;
  paymentRate: number;
}

export interface FinePaymentFormData {
  teamId: string;
  leagueId: string;
  season: string;
  amount: number;
  paymentDate: string;
  paymentMethod: FinePaymentMethod;
  referenceNumber: string | null;
  notes: string | null;
}

export interface Ranking {
  team: Team;
  rank: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  /** Final points after adjustments (backward compatible with previous 'points'). */
  points: number;
  /** Raw points before deductions/additions (optional – may be undefined if adjustments feature not loaded). */
  rawPoints?: number;
  /** Sum of all adjustments applied (can be negative or positive). */
  adjustmentPoints?: number;
  /** Optional short list of applied adjustments for UI tooltips. */
  adjustmentsSummary?: { id: string; delta: number; label: string }[];
}

// --- POINT ADJUSTMENTS / FORFAIT ---

// DB enum forfait_type uses lowercase values
export enum ForfaitType {
  HOME = 'home',
  AWAY = 'away',
  BOTH = 'both'
}

// DB enum point_adjustment_reason: 'forfait','manual','disciplinary','reversal'
export enum PointAdjustmentReason {
  FORFAIT = 'forfait',
  MANUAL = 'manual',
  DISCIPLINARY = 'disciplinary',
  REVERSAL = 'reversal'
}

export interface PointAdjustment {
  id: string;
  season: string;
  leagueGroupId: string;
  teamId: string;
  matchId?: string | null;
  reason: PointAdjustmentReason;
  deltaPoints: number; // negative or positive
  label: string;
  details?: string;
  source?: string; // 'system' | 'user' | 'commission'
  isReversal?: boolean;
  parentAdjustmentId?: string | null;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  reversedAt?: string | null;
  reversedBy?: string | null;
  reversedByName?: string | null;
}

// Lightweight settings shape (front-end mirror of backend settings) for forfait logic
export interface ForfaitSettings {
  defaultTechnicalScoreWinner: number; // e.g. 3
  defaultTechnicalScoreLoser: number; // e.g. 0
  defaultPointsDeduction: number; // e.g. -3
  applyDeductionBothOnDouble?: boolean; // default true
  allowOverrideScores?: boolean; // default false
  allowOverridePoints?: boolean; // default true
}


// --- ROLE HELPERS ---

// Helper to map DB role names to frontend UserRole enum
export const mapDbRoleToEnum = (dbRole?: string): UserRole => {
  switch (dbRole) {
    case 'super_admin': return UserRole.SUPER_ADMIN;
    case 'admin': return UserRole.ADMIN;
    case 'league_president': return UserRole.LEAGUE_PRESIDENT;
    case 'planning_manager': return UserRole.PLANNING_MANAGER;
    case 'coordinator': return UserRole.COORDINATOR;
    case 'accounting_manager': return UserRole.ACCOUNTING_MANAGER;
    case 'official': return UserRole.OFFICIAL;
    case 'team_manager': return UserRole.TEAM_MANAGER;
    case 'viewer': return UserRole.VIEWER;
    case 'accountant_entry': return UserRole.ACCOUNTANT_ENTRY;
    case 'accountant_validator': return UserRole.ACCOUNTANT_VALIDATOR;
    case 'accountant_closer': return UserRole.ACCOUNTANT_CLOSER;
    case 'disciplinary_advocate': return UserRole.DISCIPLINARY_ADVOCATE;
    case 'disciplinary_secretary': return UserRole.DISCIPLINARY_SECRETARY;
    case 'agent': return UserRole.AGENT;
    case 'contributor': return UserRole.CONTRIBUTOR;
    case 'refree_manager': return UserRole.REFREE_MANAGER;
    case 'security_gate': return UserRole.SECURITY_GATE;
    default: return UserRole.VIEWER; // Default to least privileged
  }
}

// Helper to map frontend UserRole enum to a displayable string
export const mapEnumToDisplayRole = (enumRole?: UserRole | string): string => {
  switch (enumRole) {
    case 'super_admin': return "Super Admin";
    case 'admin': return "Admin";
    case 'league_president': return "Président de Ligue";
    case 'planning_manager': return "Responsable Planification";
    case 'coordinator': return "Coordinateur";
    case 'accounting_manager': return "Responsable Comptable";
    case 'official': return "Officiel";
    case 'team_manager': return "Manager d'Équipe";
    case 'viewer': return "Visiteur";
    case 'accountant_entry': return "Comptable - Saisie";
    case 'accountant_validator': return "Comptable - Validateur";
    case 'accountant_closer': return "Comptable - Clôtureur";
    case 'disciplinary_advocate': return "Commissaire Disciplinaire";
    case 'disciplinary_secretary': return "Secrétaire de Commission";
    case 'agent': return "Agent";
    case 'contributor': return "Contributeur";
    case 'refree_manager': return "Responsable Arbitres";
    case 'security_gate': return "Contrôle d'Accès Stade";
    default: return "Visiteur";
  }
}

// --- SANCTION HELPERS ---
export const mapSanctionTypeToDisplay = (type?: SanctionType | string): string => {
  switch (type) {
    case SanctionType.YELLOW_CARD: return "Carton Jaune";
    case SanctionType.RED_CARD_DIRECT: return "Carton Rouge Direct";
    case SanctionType.RED_CARD_TWO_YELLOWS: return "Carton Rouge (2 Avert.)";
    case SanctionType.SUSPENSION_COMMISSION: return "Suspension (Commission)";
    case SanctionType.FINE: return "Amende";
    case SanctionType.SUSPENSION: return "Suspension";
    case SanctionType.WARNING: return "Avertissement";
    case SanctionType.REPORT: return "Rapport";
    case SanctionType.OTHER: return "Autre Infraction";
    default: return type || "Inconnu";
  }
};

export const mapDisciplinaryEntryStatusToDisplay = (
  status: DisciplinaryMatchEntryStatus
): string => {
  switch (status) {
    case DisciplinaryMatchEntryStatus.WAITING:
      return "En attente";
    case DisciplinaryMatchEntryStatus.IN_PROGRESS:
      return "En cours";
    case DisciplinaryMatchEntryStatus.COMPLETED:
      return "Terminé";
    default:
      return "En attente";
  }
};

// --- LEAGUE-SPECIFIC OFFICIAL ROLES ---
// Re-export types from league-roles module for convenience
export type {
  OfficialRoleCategory,
  LeagueOfficialRole,
  LeaguePaymentRule,
  LeagueRoleWithPayment,
  GroupedLeagueRoles,
  LeagueOfficialRoleInput,
  LeaguePaymentRuleInput
} from './types/league-roles';

// --- DISCIPLINARY REFACTOR TYPES ---
// Re-export types from disciplinary-refactor module for new relational structure
export type {
  DisciplinaryReason,
  LeagueDisciplinaryRule,
  LeagueDisciplinaryRuleDetail,
  DisciplinaryReasonWithStats,
  ActiveLeagueDisciplinaryRule,
  VLeagueDisciplinaryRulesDetail,
  CreateDisciplinaryReasonPayload,
  UpdateDisciplinaryReasonPayload,
  UpsertLeagueDisciplinaryRulePayload,
  CreateSanctionWithReasonPayload,
  DisciplinaryReasonFilters,
  LeagueDisciplinaryRuleFilters,
  DisciplinaryReasonManagementProps,
  LeagueRuleConfigurationProps,
  GetActiveRulesResponse,
  CreateSanctionResponse,
  SuspensionConfiguration,
  SuspensionTypeValidator,
  LegacySanctionReason,
  DisciplinaryReasonMigrationMap
} from './types/disciplinary-refactor.types';




